import { getStroke } from 'perfect-freehand'
import { PointData, CanvasSource, Texture, Sprite, Matrix } from 'pixi.js';
import { Account } from 'src/sync/account';
import { View } from './view';
import { Room } from 'src/sync/room';
import { Array as YArray } from 'yjs';
import { IAttendee } from 'src/sync/_api';
import { signal } from '@angular/core';
import { Util } from 'src/util/util';

export type Stroke = {
    points: number[][],
    erase: boolean,
    time: number,
}

export class Graffiti {
    public static readonly size = signal<number>(10, {});
    private static input: PointData[] = [];
    private static baseTexture = new CanvasSource({ width: window.innerWidth, height: window.innerHeight, });
    private static context = Graffiti.baseTexture.context2D;
    private static texture = Texture.from(Graffiti.baseTexture);
    private static cachedTransform: Matrix;
    private static previewStroke?: number[][];
    private static debounce?: NodeJS.Timeout;
    private static drawThrottle = false;

    public static options = {
        thinning: 0.5,
        smoothing: 0.5,
        streamline: 0.5,
    };

    public static create() {
        Graffiti.baseTexture.context2D.fillRect(0, 0, Graffiti.baseTexture.width, Graffiti.baseTexture.height);
        const sprite = Sprite.from(Graffiti.texture);
        sprite.eventMode = "none";

        Room.strokes.observeDeep(() => Graffiti.redraw());
        View.viewport.on('frame-end', (event) => {
            if (Util.equal(Graffiti.cachedTransform, event.worldTransform)) return;
            Graffiti.cachedTransform = event.worldTransform.clone();
            Graffiti.redraw();
        });

        return sprite;
    }

    public static strokeTo(position: PointData, preview: boolean, mouseFactor: number) {
        const last = Graffiti.input.at(-1);
        if (last && last.x === position.x && last.y === position.y) return;

        Graffiti.input.push(position);

        Graffiti.throttle(() => {
            const stroke = getStroke(Graffiti.input, { ...Graffiti.options, size: this.size() * mouseFactor });
            Graffiti.previewStroke = stroke;
            Graffiti.redraw(preview);
        });

        clearTimeout(Graffiti.debounce);
        Graffiti.debounce = setTimeout(() => {
            Graffiti.finish(preview, mouseFactor);
        }, 40);
    }

    // TODO: This method gets called 3 times on every stroke. Fix this.
    public static redraw(preview = false) {
        Graffiti.context.clearRect(0, 0, Graffiti.baseTexture.width, Graffiti.baseTexture.height);

        const attendance = Room.attendance.toJSON();
        const allStrokes: { stroke: Stroke, color: string }[] = [];
        for (const id in attendance) {
            const strokes = Room.strokes.getOrCreate(id, () => new YArray<Stroke>());
            const coloredStrokes = strokes.map(s => ({ stroke: s, color: attendance[id].color }));
            allStrokes.push(...coloredStrokes);
        }

        allStrokes.sort((lhs, rhs) => {
            return lhs.stroke.time - rhs.stroke.time;
        });

        for (const coloredStroke of allStrokes) {
            Graffiti.draw(coloredStroke.stroke, coloredStroke.color);
        }

        if (Graffiti.previewStroke) {
            Graffiti.draw({ points: Graffiti.previewStroke, erase: preview ?? false, time: Date.now() }, Account.settings.get().color);
        }

        Graffiti.baseTexture.update();
    }

    public static draw(stroke: Stroke, color: string) {
        if (stroke.points.length < 2) return;

        const viewportTransform = View.viewport.worldTransform;

        Graffiti.context.save();
        Graffiti.context.setTransform(viewportTransform.a, viewportTransform.b, viewportTransform.c, viewportTransform.d, viewportTransform.tx, viewportTransform.ty);
        Graffiti.context.globalCompositeOperation = stroke.erase ? 'destination-out' : 'source-over';
        Graffiti.context.beginPath();
        Graffiti.context.moveTo((stroke.points[0][0]), (stroke.points[0][1]));
        for (let i = 1; i < stroke.points.length; i++) {
            Graffiti.context.lineTo((stroke.points[i][0]), (stroke.points[i][1]));
        }
        Graffiti.context.fillStyle = color;
        Graffiti.context.fill();
        Graffiti.context.restore();
    }

    public static finish(previewStrokeErasing: boolean, mouseFactor: number) {
        if (!Graffiti.input.length) return;
        const finalStroke = getStroke(Graffiti.input, { ...Graffiti.options, last: true, size: this.size() * mouseFactor });
        const strokes = Room.strokes.getOrCreate(Account.id, () => new YArray<Stroke>());
        strokes.push([{
            points: finalStroke,
            erase: previewStrokeErasing,
            time: Date.now(),
        }]);
        Graffiti.input = [];
        Graffiti.previewStroke = undefined;
        Graffiti.redraw();
    }

    private static throttle(callback: Function) {
        if (Graffiti.drawThrottle) return;
        Graffiti.drawThrottle = true;

        setTimeout(() => {
            callback();
            Graffiti.drawThrottle = false;
        }, 8);
    };

    public static clear() {
        Room.strokes.clear();
        Graffiti.redraw();
    }
}
